/**
 * PrivacyPromisePage · /voice-library/privacy
 *
 * The fuller version of the "Private to you" strip. A calm,
 * unhurried page that spells out exactly what happens to a user's
 * voice recordings and their written affirmations. No jargon. No
 * legalese. A promise the user can screenshot and hold on to.
 *
 * Linked from the small privacy strip inside VoiceLibraryPage and
 * the AskAiJourneyModal Review step.
 */
import { goBack } from "../../components/SoulumoHeader";
import { useNavigate } from "react-router-dom";
import React from "react";
import { ArrowLeft, Lock, Shield, UserX, Trash2, Fingerprint, KeyRound } from "lucide-react";

const TOK = {
  pageBg: "linear-gradient(180deg, #0d1f3a 0%, #08172d 100%)",
  ink: "#0d1f3a",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.45)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  goldGlow: "rgba(231,201,138,0.16)",
};

const PROMISES = [
  {
    icon: Lock,
    title: "Everything you write stays on your account",
    body:
      "Every custom affirmation, every prompt you type into Soulumo, every line you edit or add in your own words — all of it is stored only on your private installation. It is never shared with other users, and it is never added to the library that Soulumo draws from.",
  },
  {
    icon: Fingerprint,
    title: "Every recording is stored under your identity",
    body:
      "The recordings of your voice live in secure object storage, tagged to your account and no one else's. They are only ever served back to you, only after your login has been verified. If someone else tried to fetch them, the app would answer with a plain 'not found' — even to an admin.",
  },
  {
    icon: UserX,
    title: "No other user can see any of it",
    body:
      "Reading a journey — including its title, its lines, its recorded audio — requires the request to come from the same account that made it. The check runs on every single read and write. There is no path in the app that lets one user browse another user's installation.",
  },
  {
    icon: Shield,
    title: "Admin tools only see the shared library — never your words",
    body:
      "The admin dashboard shows counts, popularity of library themes, and system health. It does not surface individual users' custom prompts or personalised lines. The only way a private line ever becomes part of the shared library is if it is explicitly promoted by an admin — a deliberate action, never an automatic one.",
  },
  {
    icon: Trash2,
    title: "Delete means delete",
    body:
      "Every recording has a Remove button in your Voice Library. When you press it, the row is taken out of your library and stops being served. You can also delete an entire installation whenever you like — the words go with it.",
  },
  {
    icon: KeyRound,
    title: "Your voice is only ever used to build your own installations",
    body:
      "When Soulumo weaves a track together, it looks in your own voice library first — so any line you have spoken becomes part of your future installations, in your voice. Your voice is never used to build anyone else's audio.",
  },
];

export default function PrivacyPromisePage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: TOK.pageBg }} data-testid="privacy-promise-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        {/* Header row — the global HOME button in the app chrome
            covers navigation; a second BACK button here overlaps
            it visually (iter265 scar) so we render only the
            "Reclaim · Private" tag on the right. */}
        <div className="flex items-center justify-end mb-6">
          <div
            className="uppercase tracking-[0.4em] text-[0.55rem]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            Soulumo · Private
          </div>
        </div>

        {/* Hero */}
        <div
          className="rounded-3xl p-6 sm:p-8"
          style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}
        >
          <Lock size={22} style={{ color: TOK.gold }} />
          <h1
            className="mt-3 font-light leading-[1.05]"
            style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2.4rem", color: TOK.cream }}
            data-testid="privacy-promise-title"
          >
            How your words are kept private
          </h1>
          <p
            className="mt-3 italic leading-relaxed max-w-lg"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.06rem" }}
          >
            The words you write here — and the recordings of your voice speaking them — are treated as yours. The whole of them, always. This page tells you exactly what that means.
          </p>
        </div>

        {/* Promises */}
        <div className="mt-8 space-y-3" data-testid="privacy-promise-list">
          {PROMISES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                className="rounded-2xl p-5"
                style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
                data-testid={`privacy-promise-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center"
                    style={{ background: TOK.goldGlow, border: `1px solid ${TOK.goldMist}`, color: TOK.gold }}
                    aria-hidden
                  >
                    <Icon size={14} />
                  </div>
                  <div className="flex-1">
                    <div
                      style={{
                        fontFamily: '"Cormorant Garamond", serif',
                        color: TOK.cream,
                        fontSize: "1.24rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {p.title}
                    </div>
                    <p
                      className="mt-1.5 italic leading-relaxed"
                      style={{
                        color: TOK.creamDim,
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: "1rem",
                      }}
                    >
                      {p.body}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sign-off */}
        <div
          className="mt-8 px-5 py-5 rounded-2xl"
          style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.gold}` }}
          data-testid="privacy-promise-signoff"
        >
          <p
            className="italic leading-relaxed"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.1rem" }}
          >
            If any of this ever stops feeling true, we would rather you know than not know. You can reach out inside the app — the same account keeps every message private.
          </p>
          <p
            className="mt-3 text-[0.66rem] uppercase tracking-[0.3em]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            — Soulumo
          </p>
        </div>

        <button
          onClick={() => goBack(nav, "/voice-library")}
          data-testid="privacy-promise-return"
          className="mt-8 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
        >
          <ArrowLeft size={13} /> Return
        </button>
      </div>
    </div>
  );
}
